import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Order, OrderStatus } from '../lib/types';
import { getOrderAuditLog } from '../lib/api';
import type { OrderAuditEntry } from '../lib/api';
import { ConfirmModal } from './ui';
import { DashboardTabProps, SortColumn, SortDir, ALL_STATUSES, ConfirmState } from './dashboard/types';
import { OrdersTable } from './dashboard/OrdersTable';
import { StatusDropdown } from './dashboard/StatusDropdown';
import { ThreeDotMenu } from './dashboard/ThreeDotMenu';
import { StockModal } from './dashboard/StockModal';
import { EditOrderModal } from './dashboard/EditOrderModal';
import { importOrdersCSV } from './dashboard/csvImport';

// =============================================================================
// DashboardTab — thin orchestrator. Heavy lifting lives in ./dashboard/*.
// =============================================================================

export function DashboardTab({
  filteredOrders, locale, currency, onStatusChange, onDelete,
  departments, staffDirectory, categories, vendors, onOrderAdded, showToast,
  addOrderTrigger, importTrigger, onInventoryAdd, onInventoryChanged,
  inventory, serverPagination, onPageChange,
}: DashboardTabProps) {

  // Row expansion + menu anchors
  const [expandedId,  setExpandedId]  = useState<string | null>(null);
  const [rowMenuId,   setRowMenuId]   = useState<string | null>(null);
  const [rowMenuPos,  setRowMenuPos]  = useState<{ top: number; right: number; flipUp: boolean; btnTop: number }>({ top: 0, right: 0, flipUp: false, btnTop: 0 });

  // Status dropdown
  const [statusDropdownId, setStatusDropdownId] = useState<string | null>(null);
  const [statusDropPos,    setStatusDropPos]    = useState<{ top: number; left: number; btnTop: number }>({ top: 0, left: 0, btnTop: 0 });

  // Confirm modal
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const showConfirm = useCallback((message: string, onConfirm: () => void, label = 'Confirm', destructive = false) => {
    setConfirmState({ message, label, destructive, onConfirm });
  }, []);

  // Audit log
  const [auditOrderId, setAuditOrderId] = useState<string | null>(null);
  const [auditLog,     setAuditLog]     = useState<OrderAuditEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const toggleAuditLog = useCallback(async (orderId: string) => {
    if (auditOrderId === orderId) { setAuditOrderId(null); return; }
    setAuditOrderId(orderId);
    setAuditLoading(true);
    const res = await getOrderAuditLog(orderId);
    setAuditLog(res.ok && res.data ? res.data : []);
    setAuditLoading(false);
  }, [auditOrderId]);

  // Stock modal
  const [stockOrder, setStockOrder] = useState<Order | null>(null);

  // Add/Edit order modal
  const [showAddEdit, setShowAddEdit] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Order | null>(null);

  // Sort state
  const [sortCol, setSortCol] = useState<SortColumn | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const handleSort = useCallback((col: SortColumn) => {
    if (sortCol === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }, [sortCol, sortDir]);

  // CSV import
  const csvRef = useRef<HTMLInputElement>(null);
  const lastImport = useRef(importTrigger || 0);
  useEffect(() => {
    if (importTrigger && importTrigger > lastImport.current && csvRef.current) {
      lastImport.current = importTrigger;
      csvRef.current.click();
    }
  }, [importTrigger]);

  // Add order trigger from parent header
  const lastAddTrigger = useRef(addOrderTrigger || 0);
  useEffect(() => {
    if (addOrderTrigger && addOrderTrigger > lastAddTrigger.current) {
      lastAddTrigger.current = addOrderTrigger;
      setEditingOrder(null);
      setShowAddEdit(true);
    }
  }, [addOrderTrigger]);

  const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { created, failed, errors } = await importOrdersCSV(file, departments, categories);
      if (errors.length > 0 && created === 0) {
        showToast(`Import failed: ${errors[0]}`);
      } else {
        const msg = `Imported ${created} order${created !== 1 ? 's' : ''}` +
          (failed > 0 ? `, ${failed} failed` : '') +
          (errors.length > 0 ? `, ${errors.length} row${errors.length !== 1 ? 's' : ''} skipped` : '');
        showToast(msg, failed === 0 && errors.length === 0 ? 'success' : 'error');
        if (created > 0) onOrderAdded();
      }
    } catch (err) {
      showToast(`Import error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
    if (csvRef.current) csvRef.current.value = '';
  };

  // Row interactions
  const handleStatusClick = (order: Order, e: React.MouseEvent) => {
    if (statusDropdownId === order.id) { setStatusDropdownId(null); return; }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setStatusDropPos({ top: rect.bottom + 4, left: rect.left, btnTop: rect.top });
    setStatusDropdownId(order.id);
  };

  const handleStatusSelect = (order: Order, newStatus: OrderStatus) => {
    const currentIdx = ALL_STATUSES.indexOf(order.status);
    const newIdx = ALL_STATUSES.indexOf(newStatus);
    if (newStatus === order.status) { setStatusDropdownId(null); return; }

    const doChange = () => { setStatusDropdownId(null); onStatusChange(order, newStatus); };

    if (order.status === 'Closed') {
      setStatusDropdownId(null);
      showConfirm('This order was completed. Would you like to reopen it for further changes?', doChange, 'Reopen');
    } else if (newIdx < currentIdx) {
      setStatusDropdownId(null);
      showConfirm(`Update status from ${order.status} back to ${newStatus}?`, doChange, 'Update');
    } else if (newStatus === 'Closed') {
      setStatusDropdownId(null);
      showConfirm('Mark this order as complete? It will be included in your reports.', doChange, 'Complete');
    } else {
      doChange();
    }
  };

  const handleMenuClick = (order: Order, e: React.MouseEvent) => {
    if (rowMenuId === order.id) { setRowMenuId(null); return; }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // Three-dot menu is ~220px tall (4 items × ~46px including separator + padding).
    // If less than 250px clearance below the anchor, flip upward.
    const MENU_HEIGHT = 250;
    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < MENU_HEIGHT;
    setRowMenuPos({
      top: rect.bottom + 4,
      btnTop: rect.top,
      right: window.innerWidth - rect.right,
      flipUp,
    });
    setRowMenuId(order.id);
  };

  const handleToggleExpand = (id: string) => {
    setExpandedId(prev => (prev === id ? null : id));
    setAuditOrderId(null);
  };

  const handleDeleteConfirmed = (id: string) => {
    showConfirm(
      'Delete this order? This cannot be undone.',
      () => onDelete(id),
      'Delete',
      true,
    );
  };

  // Lookup helpers for stock modal
  const canStock = (o: Order) => o.status === 'Delivered' && (o.order_items ?? []).length > 0 && !o.stocked_at;
  const isStocked = (o: Order) => !!o.stocked_at;

  // Look up the active order by id from the current page (for floating overlays)
  const activeStatusOrder = statusDropdownId ? filteredOrders.find(o => o.id === statusDropdownId) : null;
  const activeMenuOrder   = rowMenuId        ? filteredOrders.find(o => o.id === rowMenuId)        : null;

  return (
    <div className="pane active pane-enter">
      <input
        ref={csvRef}
        type="file"
        accept=".csv,.txt"
        style={{ display: 'none' }}
        onChange={handleImportCSV}
      />

      {/* Confirm modal */}
      {confirmState && (
        <ConfirmModal
          open
          message={confirmState.message}
          confirmLabel={confirmState.label}
          destructive={confirmState.destructive}
          onConfirm={() => { confirmState.onConfirm(); setConfirmState(null); }}
          onCancel={() => setConfirmState(null)}
        />
      )}

      {/* Stock modal */}
      {stockOrder && (
        <StockModal
          order={stockOrder}
          inventory={inventory ?? []}
          departments={departments}
          onInventoryAdd={onInventoryAdd}
          onInventoryChanged={onInventoryChanged}
          onOrderChanged={onOrderAdded}
          showToast={showToast}
          onClose={() => setStockOrder(null)}
        />
      )}

      {/* Status dropdown */}
      {activeStatusOrder && (
        <StatusDropdown
          order={activeStatusOrder}
          anchor={statusDropPos}
          onSelect={handleStatusSelect}
          onClose={() => setStatusDropdownId(null)}
        />
      )}

      {/* Three-dot menu */}
      {activeMenuOrder && (
        <ThreeDotMenu
          order={activeMenuOrder}
          anchor={rowMenuPos}
          canStock={canStock(activeMenuOrder)}
          isStocked={isStocked(activeMenuOrder)}
          onEdit={(o) => { setEditingOrder(o); setShowAddEdit(true); }}
          onViewHistory={(o) => { setExpandedId(o.id); toggleAuditLog(o.id); }}
          onStock={(o) => setStockOrder(o)}
          onDelete={handleDeleteConfirmed}
          onClose={() => setRowMenuId(null)}
        />
      )}

      {/* Add/Edit order modal */}
      {showAddEdit && (
        <EditOrderModal
          open={showAddEdit}
          onClose={() => { setShowAddEdit(false); setEditingOrder(null); }}
          editingOrder={editingOrder}
          departments={departments}
          staffDirectory={staffDirectory}
          categories={categories}
          vendors={vendors}
          currency={currency}
          showToast={showToast}
          onSaved={onOrderAdded}
          onDuplicate={(message, retry) => {
            showConfirm(`${message} Do you want to add it anyway?`, retry, 'Add Anyway');
          }}
        />
      )}

      <OrdersTable
        orders={filteredOrders}
        currency={currency}
        locale={locale}
        sortCol={sortCol}
        sortDir={sortDir}
        onSort={handleSort}
        expandedId={expandedId}
        onToggleExpand={handleToggleExpand}
        onStatusClick={handleStatusClick}
        onMenuClick={handleMenuClick}
        auditOrderId={auditOrderId}
        auditLoading={auditLoading}
        auditLog={auditLog}
        onToggleAudit={toggleAuditLog}
        serverPagination={serverPagination}
        onPageChange={onPageChange}
        onAddOrder={() => { setEditingOrder(null); setShowAddEdit(true); }}
      />
    </div>
  );
}
